import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase.config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { SUPPORTED_LANGUAGES, getTranslation, getNestedTranslation, DEFAULT_LANGUAGE } from '../locales';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
  const [isLoading, setIsLoading] = useState(true);

  // Load user's language preference from Firestore
  useEffect(() => {
    const loadUserLanguage = async () => {
      try {
        if (auth.currentUser) {
          // Try users collection first (where UserProfile saves)
          const userRef = doc(db, 'users', auth.currentUser.uid);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const userLanguage = userData.language || DEFAULT_LANGUAGE;
            setCurrentLanguage(userLanguage);
            console.log('Language loaded from users collection:', userLanguage);
          } else {
            // Fallback to user_settings collection
            const settingsRef = doc(db, 'user_settings', auth.currentUser.uid);
            const settingsSnap = await getDoc(settingsRef);
            
            if (settingsSnap.exists()) {
              const settingsData = settingsSnap.data();
              const userLanguage = settingsData.language || DEFAULT_LANGUAGE;
              setCurrentLanguage(userLanguage);
              console.log('Language loaded from user_settings collection:', userLanguage);
            }
          }
        }
      } catch (error) {
        console.error('Error loading user language:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserLanguage();
  }, []);

  // Save language preference to Firestore
  const changeLanguage = async (languageCode) => {
    try {
      if (auth.currentUser && SUPPORTED_LANGUAGES[languageCode]) {
        setCurrentLanguage(languageCode);
        
        // Save to users collection (same as UserProfile)
        const userRef = doc(db, 'users', auth.currentUser.uid);
        await updateDoc(userRef, {
          language: languageCode,
          updatedAt: new Date()
        });
        
        console.log('Language saved to users collection:', languageCode);
        
        // Apply font family to document
        applyLanguageFont(languageCode);
      }
    } catch (error) {
      console.error('Error saving language preference:', error);
    }
  };

  // Apply language-specific font
  const applyLanguageFont = (languageCode) => {
    const languageInfo = SUPPORTED_LANGUAGES[languageCode];
    if (languageInfo && languageInfo.fontFamily) {
      document.documentElement.style.fontFamily = languageInfo.fontFamily;
    }
  };

  // Translation function
  const t = (key, fallback = true) => {
    return getTranslation(key, currentLanguage, fallback);
  };

  // Nested translation function
  const tn = (path, fallback = true) => {
    return getNestedTranslation(path, currentLanguage, fallback);
  };

  // Get current language info
  const getCurrentLanguageInfo = () => {
    return SUPPORTED_LANGUAGES[currentLanguage] || SUPPORTED_LANGUAGES[DEFAULT_LANGUAGE];
  };

  // Apply font when language changes
  useEffect(() => {
    applyLanguageFont(currentLanguage);
  }, [currentLanguage]);

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    tn,
    getCurrentLanguageInfo,
    isLoading,
    supportedLanguages: SUPPORTED_LANGUAGES
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

